import React from "react";
import { OTelSpan } from "../types";
import { Wrench } from "lucide-react";

interface ToolSpanViewProps {
  span: OTelSpan;
}

const safeParseJSON = (val?: string | object) => {
  if (!val) return null;
  if (typeof val === "object") return val;
  try {
    return JSON.parse(val);
  } catch {
    return null;
  }
};

export const ToolSpanView: React.FC<ToolSpanViewProps> = ({ span }) => {
  // Support both OpenInference (tool.*) and pydantic-ai (gen_ai.tool.*) conventions
  const toolName =
    span.attributes["tool.name"] ??
    span.attributes["gen_ai.tool.name"] ??
    span.name;
  const toolDesc = span.attributes["tool.description"];
  const toolCallId = span.attributes["gen_ai.tool.call.id"];

  // Arguments: OpenInference tool.parameters or pydantic-ai gen_ai.tool.call.arguments
  const rawArgs =
    span.attributes["tool.parameters"] ??
    span.attributes["gen_ai.tool.call.arguments"];
  const parsedArgs = safeParseJSON(rawArgs as string | object | undefined);

  // Result: pydantic-ai gen_ai.tool.call.result
  const rawResult = span.attributes["gen_ai.tool.call.result"];
  const parsedResult = safeParseJSON(rawResult as string | object | undefined);

  // Also check generic input/output
  const inputValue = span.attributes["input.value"];
  const outputValue =
    span.attributes["output.value"] ?? span.attributes["final_result"];

  const hasArgs = parsedArgs || rawArgs;
  const hasResult = parsedResult || rawResult || outputValue;

  return (
    <div className="flex flex-col h-full bg-white p-5 overflow-y-auto">
      {/* Tool header */}
      <div className="flex items-center gap-3 mb-6">
        <div className="w-8 h-8 rounded-md bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
          <Wrench className="w-4 h-4 text-emerald-500" />
        </div>
        <div>
          <h3 className="text-[#0f172a] font-medium tracking-tight text-lg">
            {toolName}
          </h3>
          {toolDesc && (
            <p className="text-[#64748b] text-sm mt-0.5">{toolDesc}</p>
          )}
          {toolCallId && (
            <p className="text-[10px] font-mono text-[#94a3b8] mt-0.5">
              {toolCallId}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-5">
        {/* Arguments */}
        {hasArgs && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-2">
              Arguments
            </h4>
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-md p-4 overflow-x-auto">
              <pre className="text-sm font-mono text-[#0f172a] whitespace-pre-wrap">
                {parsedArgs
                  ? JSON.stringify(parsedArgs, null, 2)
                  : String(rawArgs)}
              </pre>
            </div>
          </div>
        )}

        {/* Input (fallback) */}
        {!hasArgs && inputValue && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-2">
              Input
            </h4>
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-md p-4 overflow-x-auto">
              <pre className="text-sm font-mono text-[#0f172a] whitespace-pre-wrap">
                {typeof inputValue === "string"
                  ? safeParseJSON(inputValue)
                    ? JSON.stringify(safeParseJSON(inputValue), null, 2)
                    : inputValue
                  : JSON.stringify(inputValue, null, 2)}
              </pre>
            </div>
          </div>
        )}

        {/* Result */}
        {hasResult && (
          <div>
            <h4 className="text-xs font-semibold uppercase tracking-wider text-[#64748b] mb-2">
              Result
            </h4>
            <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-md p-4 overflow-x-auto max-h-[400px]">
              <pre className="text-sm font-mono text-[#0f172a] whitespace-pre-wrap">
                {parsedResult
                  ? JSON.stringify(parsedResult, null, 2)
                  : String(rawResult ?? outputValue)}
              </pre>
            </div>
          </div>
        )}

        {/* Nothing */}
        {!hasArgs && !hasResult && !inputValue && (
          <div className="text-sm text-[#64748b]">
            No tool call data recorded for this span.
          </div>
        )}
      </div>
    </div>
  );
};
